

import { useUserStore } from '../../app/store/useUserStore';

export type KycStatusType =
  | 'pending'
  | 'unverified'
  | 'verified'
  | 'canceled'
  | 'restricted'
  | 'requires_input'
  | 'processing';

export interface KycVerificationStatus {
  isKycStatusVerified: boolean;
  isCardVerified: boolean;
  isFullyVerified: boolean;
  missingVerifications: ('kyc' | 'card')[];
  kycStatus: KycStatusType | undefined;
  isKycRejected: boolean;
  isKycPending: boolean;
}

/**
 * Hook to check KYC verification status
 * @returns Object containing verification status details
 */
export const useKycVerification = (): KycVerificationStatus => {
  const user = useUserStore((state) => state.user);

  const kycStatus = user?.kyc_status?.toLowerCase() as KycStatusType | undefined;
  const isKycStatusVerified = kycStatus === 'verified';
  const isCardVerified = user?.card_verified ?? false;
  const isFullyVerified = isKycStatusVerified && isCardVerified;

  // Check if KYC is rejected/restricted (not just pending)
  const isKycRejected = kycStatus === 'restricted' || kycStatus === 'canceled';

  // Check if KYC is in pending/processing state
  const isKycPending = kycStatus === 'pending' || kycStatus === 'unverified' ||
                       kycStatus === 'processing' || kycStatus === 'requires_input';

  const missingVerifications: ('kyc' | 'card')[] = [];
  if (!isKycStatusVerified) missingVerifications.push('kyc');
  if (!isCardVerified) missingVerifications.push('card');

  return {
    isKycStatusVerified,
    isCardVerified,
    isFullyVerified,
    missingVerifications,
    kycStatus,
    isKycRejected,
    isKycPending,
  };
};

/**
 * Check if a route should be restricted based on KYC status
 * @param pathname Current route pathname
 * @param isKycVerified Whether user has completed KYC
 * @returns true if route should be blocked, false if allowed
 */
export const shouldRestrictRoute = (pathname: string, isKycVerified: boolean): boolean => {
  // If already verified, no restrictions
  if (isKycVerified) return false;

  // Define allowed routes even without KYC verification
  // These are the ONLY routes accessible without full verification
  const allowedPatterns = [
    // Auth routes
    '/login',
    '/logout',
    '/signup',
    '/callback',

    // Item CRUD (Sender) - Create, View, Edit, Delete allowed
    '/create-shipment',
    '/my-items',
    '/my-orders', // Same as my-items for orders view

    // Itinerary CRUD (Traveller) - Create, View, Edit, Delete allowed
    '/create-itinerary',
    '/my-travels',

    // Search (home page and search functionality)
    '/search',
    '/explore',
    '/',

    // Profile update (users need to access profile to verify)
    '/update-profile',
    '/profile',

    // Payment result pages (users should see payment status)
    '/payment/success',
    '/payment/cancel',

    // Receiver pages (not authenticated users)
    '/receiver/',
  ];

  // Check if current pathname matches any allowed pattern
  const isExemptRoute = allowedPatterns.some(pattern => {
    if (pattern === '/') {
      // Home page: exact match only
      return pathname === '/';
    }
    // All other patterns: check if pathname starts with pattern
    return pathname.startsWith(pattern);
  });

  // If route is exempt, don't restrict (return false)
  // If route is NOT exempt, restrict (return true)
  return !isExemptRoute;
};
